import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Swal from 'sweetalert2';
import api from '../../services/api';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

function EmailTriggerModal({ tenantId, academicSessionId, selectedCourses, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Role checkboxes & templates
  const [setterEnabled, setSetterEnabled] = useState(false);
  const [setterTemplate, setSetterTemplate] = useState(null);
  const [moderatorEnabled, setModeratorEnabled] = useState(false);
  const [moderatorTemplate, setModeratorTemplate] = useState(null);
  const [valuatorEnabled, setValuatorEnabled] = useState(false);
  const [valuatorTemplate, setValuatorTemplate] = useState(null);

  const templateOptions = useMemo(
    () => templates.map((t) => ({
      value: t.TemplateID,
      label: t.TemplateName + (t.CategoryName ? ` (${t.CategoryName})` : ''),
    })),
    [templates]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/qp-settings/email-templates/${tenantId}`);
        if (res.data.success) setTemplates(res.data.data);
      } catch {
        toast.error('Failed to load email templates');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const getActiveRoles = () => {
    const roles = [];
    if (setterEnabled) roles.push('Setter');
    if (moderatorEnabled) roles.push('Moderator');
    if (valuatorEnabled) roles.push('Valuator');
    return roles;
  };

  const getAssignments = () => {
    const a = [];
    if (setterEnabled && setterTemplate) a.push({ role: 'Setter', templateId: setterTemplate.value });
    if (moderatorEnabled && moderatorTemplate) a.push({ role: 'Moderator', templateId: moderatorTemplate.value });
    if (valuatorEnabled && valuatorTemplate) a.push({ role: 'Valuator', templateId: valuatorTemplate.value });
    return a;
  };

  const handleTrigger = async () => {
    const roles = getActiveRoles();
    if (roles.length === 0) return toast.error('Please select at least one recipient type');

    if (setterEnabled && !setterTemplate) return toast.error('Please select a template for Paper Setter');
    if (moderatorEnabled && !moderatorTemplate) return toast.error('Please select a template for Paper Moderator');
    if (valuatorEnabled && !valuatorTemplate) return toast.error('Please select a template for Paper Valuator');

    const courseIds = selectedCourses.map((c) => c.CourseID);

    // Preview counts
    try {
      const previewRes = await api.post('/paper-allocations/email-preview', {
        academicSessionId,
        courseIds,
        roles,
      });

      if (!previewRes.data.success) {
        return toast.error(previewRes.data.message || 'Failed to preview');
      }

      const stats = previewRes.data.data;

      if (stats.total === 0) {
        return Swal.fire({
          title: 'No Recipients',
          text: 'No allocated users found for the selected courses and roles. Please make sure allocations are done first.',
          icon: 'info',
          confirmButtonColor: '#1a237e',
        });
      }

      // Build stats HTML
      let statsHtml = `<div style="text-align:left;font-size:14px;line-height:1.8;">`;
      statsHtml += `<strong>Courses selected:</strong> ${selectedCourses.length}<br/>`;
      statsHtml += `<strong>Total emails to send:</strong> ${stats.total}<br/><br/>`;

      if (stats.byRole.Setter > 0) {
        statsHtml += `<span style="color:#1565c0;">Paper Setters:</span> ${stats.byRole.Setter} email(s) to ${stats.uniqueByRole.Setter} user(s)<br/>`;
      }
      if (stats.byRole.Moderator > 0) {
        statsHtml += `<span style="color:#6a1b9a;">Paper Moderators:</span> ${stats.byRole.Moderator} email(s) to ${stats.uniqueByRole.Moderator} user(s)<br/>`;
      }
      if (stats.byRole.Valuator > 0) {
        statsHtml += `<span style="color:#00695c;">Valuators:</span> ${stats.byRole.Valuator} email(s) to ${stats.uniqueByRole.Valuator} user(s)<br/>`;
      }
      statsHtml += `</div>`;

      const confirm = await Swal.fire({
        title: 'Confirm Email Trigger',
        html: statsHtml,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1a237e',
        cancelButtonColor: '#616161',
        confirmButtonText: `Yes, send ${stats.total} email(s)`,
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      // Send
      setSending(true);
      const sendRes = await api.post('/paper-allocations/send-emails', {
        academicSessionId,
        courseIds,
        assignments: getAssignments(),
      });

      if (sendRes.data.success) {
        const d = sendRes.data.data;
        let resultIcon = 'success';
        let resultTitle = 'Emails Triggered';
        let resultText = `${d.sent} email(s) sent successfully.`;
        if (d.failed > 0) {
          resultIcon = 'warning';
          resultTitle = 'Emails Partially Sent';
          resultText = `${d.sent} sent, ${d.failed} failed.`;
        }

        await Swal.fire({
          title: resultTitle,
          text: resultText,
          icon: resultIcon,
          confirmButtonColor: '#1a237e',
        });

        onSent();
      } else {
        toast.error(sendRes.data.message || 'Failed to send emails');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to trigger emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 600, maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <h2>Trigger Emails</h2>
        <p style={{ color: '#616161', fontSize: 13, marginTop: -8 }}>
          {selectedCourses.length} course(s) selected
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading templates...</div>
        ) : templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#e65100', background: '#fff3e0', borderRadius: 6 }}>
            No email templates found. Please create a template in QP Settings → Email Templates first.
          </div>
        ) : (
          <>
            {/* Paper Setter */}
            <div className="email-role-row">
              <label className="checkbox-inline email-role-check">
                <input
                  type="checkbox"
                  checked={setterEnabled}
                  onChange={(e) => { setSetterEnabled(e.target.checked); if (!e.target.checked) setSetterTemplate(null); }}
                />
                <strong>Paper Setter</strong>
              </label>
              {setterEnabled && (
                <div style={{ flex: 1 }}>
                  <Select
                    options={templateOptions}
                    value={setterTemplate}
                    onChange={setSetterTemplate}
                    placeholder="Select Email Template..."
                    isSearchable
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>

            {/* Paper Moderator */}
            <div className="email-role-row">
              <label className="checkbox-inline email-role-check">
                <input
                  type="checkbox"
                  checked={moderatorEnabled}
                  onChange={(e) => { setModeratorEnabled(e.target.checked); if (!e.target.checked) setModeratorTemplate(null); }}
                />
                <strong>Paper Moderator</strong>
              </label>
              {moderatorEnabled && (
                <div style={{ flex: 1 }}>
                  <Select
                    options={templateOptions}
                    value={moderatorTemplate}
                    onChange={setModeratorTemplate}
                    placeholder="Select Email Template..."
                    isSearchable
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>

            {/* Paper Valuator */}
            <div className="email-role-row">
              <label className="checkbox-inline email-role-check">
                <input
                  type="checkbox"
                  checked={valuatorEnabled}
                  onChange={(e) => { setValuatorEnabled(e.target.checked); if (!e.target.checked) setValuatorTemplate(null); }}
                />
                <strong>Paper Valuator</strong>
              </label>
              {valuatorEnabled && (
                <div style={{ flex: 1 }}>
                  <Select
                    options={templateOptions}
                    value={valuatorTemplate}
                    onChange={setValuatorTemplate}
                    placeholder="Select Email Template..."
                    isSearchable
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>

            <div className="form-actions" style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16, marginTop: 16 }}>
              <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-save"
                onClick={handleTrigger}
                disabled={sending || (!setterEnabled && !moderatorEnabled && !valuatorEnabled)}
              >
                {sending ? 'Sending...' : 'Trigger Emails'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EmailTriggerModal;
